import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { FirebaseContext } from "./FirebaseProvider";
import { FirebaseProviderData, MarketProviderData } from "../../types/Providers";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { AuthContext } from "./AuthProvider";
import { AuthProviderData } from "../../types/Providers";

import { SubscriptionInfo } from "../../types/Market";
import { NetworkContext } from "./NetworkProvider";
import toast from "react-hot-toast";
import { didUserPayAnItemFullPriceAPI, PaymentsAPI } from "../../utilities/network/api";
import { DidUserPayAnItemFullPriceAPIResponse, RewardItemPurchaseAPIResponse } from "../../types/APIResponse";
import { useTranslation } from "react-i18next";
import { Inventory } from "../../types/Inventory";

export const MarketContext = createContext<MarketProviderData | null>(null);

export const MarketProvider = (props): JSX.Element => {
	const children = props.children;
	const { t: tDashboard } = useTranslation("dashboard");
	const { t: tResponse } = useTranslation("responses");

	const [giftCards, setGiftCards] = useState<{ [country: string]: number[] }>();
	const [subscriptionInfo, setSubscriptionInfo] = useState({} as SubscriptionInfo);
	const [canUserApplyDiscount, setCanUserApplyDiscount] = useState(false);
	const giftcardsAvailable = [100, 200, 400];
	const VAT = 22;
	const [rates, setRates] = useState({} as Map<string, number>);
	const { makePost, makeGet } = useContext(NetworkContext);
	const oneBoltInCents = 5;

	const { profile } = useContext(AuthContext) as AuthProviderData;
	const { myFS } = useContext(FirebaseContext) as FirebaseProviderData;

	const fetchExchangeRates = useCallback(async (): Promise<void> => {
		try {
			if (profile == null) return;
			const querySnapshot = await getDocs(collection(myFS, "exchangeRates"));
			let rates = new Map();
			querySnapshot.forEach((doc) => {
				Object.keys(doc.data()).forEach((keys) => {
					if (keys === "lastcheck") return;
					rates.set(keys as string, doc.data()[keys]);
				});
				setRates(rates);
			});
		} catch (err) {
			console.log(err);
		}
	}, [profile, myFS]);

	const fetchSubscription = useCallback(async (): Promise<void> => {
		const docRef = doc(myFS, "inventory/subscription");
		const querySnapSubscription = await getDoc(docRef);
		setSubscriptionInfo(querySnapSubscription.data() as SubscriptionInfo);
	}, [myFS]);

	const didUserPayAnItemFullPrice = useCallback(async () => {
		if (profile == null) return false;

		const res = await makeGet<DidUserPayAnItemFullPriceAPIResponse>(`${didUserPayAnItemFullPriceAPI}?_token=${profile.idToken}`);

		if (res.status === 200) {
			setCanUserApplyDiscount(res.data.success);
			return res.data.success;
		} else {
			toast.error(tResponse(res.data.i18nKey));
			setCanUserApplyDiscount(false);
			return false;
		}
	}, [profile]);

	// const fetchGiftCard = async (): Promise<void> => {
	// 	const items = await getItemsFromDB();

	// 	const giftCards = {} as { [country: string]: number[] };

	// 	Object.entries(items.giftCards.amazon).forEach(([country, { amount }]) => {
	// 		if (amount) giftCards[country] = amount;
	// 	});

	// 	setGiftCards(giftCards);
	// };

	useEffect(() => {
		if (profile != null) {
			fetchExchangeRates();
			// fetchGiftCard();
			fetchSubscription();
			didUserPayAnItemFullPrice();
		}
	}, [profile, fetchExchangeRates, fetchSubscription, didUserPayAnItemFullPrice]);

	const getItemsFromDB = async (): Promise<Inventory.Items> => {
		return (await getDoc(doc(myFS, "inventory/items"))).data() as Inventory.Items;
	};

	const buySubscriptionWithBolts = async (): Promise<void> => {
		const res = await makePost(PaymentsAPI.subscription, {
			_token: `Bearer ${profile.idToken}`,
		});
		if (res.status === 200) {
			toast.success(res.data.message);
		} else {
			toast.error(res.data.message);
		}
	};

	const marketProviderData: MarketProviderData = {
		giftCards,
		rates,
		oneBoltInCents,
		subscriptionInfo,
		giftcardsAvailable,
		getItemsFromDB,
		buySubscriptionWithBolts,
		canUserApplyDiscount,
		setCanUserApplyDiscount,
		VAT,
	};

	return <MarketContext.Provider value={marketProviderData}>{children}</MarketContext.Provider>;
};
